import { CommonModule } from '@angular/common';
import { Component, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HierarchyType, SbcApiService, SystemCodeValue } from '../services/sbc-api.service';
import { SessionService } from '../../../core/services/session.service';
import { StatusBadgeComponent } from '../components/status-badge.component';

import { PageBackButtonComponent } from '../../../shared/components/page-back-button/page-back-button.component';
import { PageRefreshButtonComponent } from '../../../shared/components/page-refresh-button/page-refresh-button.component';

@Component({
  selector: 'app-administration',
  standalone: true,
  imports: [CommonModule, FormsModule, StatusBadgeComponent, PageRefreshButtonComponent, PageBackButtonComponent],
  templateUrl: './administration.component.html',
  styleUrl: './administration.component.css',
})
export class AdministrationComponent {
  private readonly api = inject(SbcApiService);
  readonly session = inject(SessionService);

  activeTab = signal<'hierarchy' | 'codes'>('hierarchy');

  hierarchy = signal<HierarchyType[]>([]);
  codeValues = signal<SystemCodeValue[]>([]);
  categories = signal<HierarchyType[]>([]);
  error = signal('');
  toast = signal('');
  loadingHierarchy = signal(false);
  loadingCodes = signal(false);
  hierarchyModalOpen = signal(false);
  codeModalOpen = signal(false);
  levelFilter = 'subtype';

  hierarchyPage = signal(1);
  codePage = signal(1);
  readonly pageSize = signal(10);
  hierarchyTotal = signal(0);
  hierarchyTotalPages = signal(1);
  codeTotal = signal(0);
  codeTotalPages = signal(1);

  hierarchyForm = {
    module: 'BYELAW_NORMS',
    parentId: '',
    code: '',
    name: '',
    level: 'subtype',
    sortOrder: '0',
  };

  codeForm = {
    module: 'BYELAW_NORMS',
    codeType: 'HEIGHT_BAND',
    code: '',
    label: '',
    sortOrder: '0',
  };

  constructor() {
    effect(() => {
      this.session.authorityId();
      untracked(() => {
        this.hierarchyPage.set(1);
        this.codePage.set(1);
        this.loadCategories();
        this.reloadHierarchy();
        this.reloadCodeValues();
      });
    });
  }

  reload(): void {
    this.loadCategories();
    this.reloadHierarchy();
    this.reloadCodeValues();
  }

  setTab(tab: 'hierarchy' | 'codes'): void {
    this.activeTab.set(tab);
  }

  openHierarchyModal(): void {
    this.hierarchyModalOpen.set(true);
  }

  closeHierarchyModal(): void {
    this.hierarchyModalOpen.set(false);
  }

  openCodeModal(): void {
    this.codeModalOpen.set(true);
  }

  closeCodeModal(): void {
    this.codeModalOpen.set(false);
  }

  loadCategories(): void {
    this.api.listHierarchyTypes({ module: 'BYELAW_NORMS', level: 'category', pageSize: '100' }).subscribe({
      next: (res) => this.categories.set(res.data || []),
    });
  }

  reloadHierarchy(): void {
    this.loadingHierarchy.set(true);
    this.error.set('');
    this.api
      .listHierarchyTypes({
        module: 'BYELAW_NORMS',
        level: this.levelFilter,
        page: String(this.hierarchyPage()),
        pageSize: String(this.pageSize()),
      })
      .subscribe({
        next: (res) => {
          this.hierarchy.set(res.data || []);
          this.hierarchyTotal.set(Number(res.meta?.total ?? res.data?.length ?? 0));
          this.hierarchyPage.set(Number(res.meta?.page ?? this.hierarchyPage()));
          this.hierarchyTotalPages.set(Number(res.meta?.totalPages ?? 1));
          this.loadingHierarchy.set(false);
        },
        error: (err) => {
          this.loadingHierarchy.set(false);
          this.error.set(err?.error?.message || 'Failed to load hierarchy');
        },
      });
  }

  reloadCodeValues(): void {
    this.loadingCodes.set(true);
    this.api
      .listCodeValues({
        module: 'BYELAW_NORMS',
        page: String(this.codePage()),
        pageSize: String(this.pageSize()),
      })
      .subscribe({
        next: (res) => {
          this.codeValues.set(res.data || []);
          this.codeTotal.set(Number(res.meta?.total ?? res.data?.length ?? 0));
          this.codePage.set(Number(res.meta?.page ?? this.codePage()));
          this.codeTotalPages.set(Number(res.meta?.totalPages ?? 1));
          this.loadingCodes.set(false);
        },
        error: (err) => {
          this.loadingCodes.set(false);
          this.error.set(err?.error?.message || 'Failed to load code values');
        },
      });
  }

  onLevelFilterChange(): void {
    this.hierarchyPage.set(1);
    this.reloadHierarchy();
  }

  hierarchyRangeLabel(): string {
    return this.rangeLabel(this.hierarchyPage(), this.hierarchyTotal());
  }

  codeRangeLabel(): string {
    return this.rangeLabel(this.codePage(), this.codeTotal());
  }

  private rangeLabel(page: number, total: number): string {
    if (!total) return '0 of 0';
    const size = this.pageSize();
    const start = (page - 1) * size + 1;
    const end = Math.min(page * size, total);
    return `${start}–${end} of ${total}`;
  }

  goHierarchyFirst(): void {
    if (this.hierarchyPage() === 1) return;
    this.hierarchyPage.set(1);
    this.reloadHierarchy();
  }

  goHierarchyPrev(): void {
    if (this.hierarchyPage() <= 1) return;
    this.hierarchyPage.update((p) => p - 1);
    this.reloadHierarchy();
  }

  goHierarchyNext(): void {
    if (this.hierarchyPage() >= this.hierarchyTotalPages()) return;
    this.hierarchyPage.update((p) => p + 1);
    this.reloadHierarchy();
  }

  goHierarchyLast(): void {
    if (this.hierarchyPage() >= this.hierarchyTotalPages()) return;
    this.hierarchyPage.set(this.hierarchyTotalPages());
    this.reloadHierarchy();
  }

  goCodeFirst(): void {
    if (this.codePage() === 1) return;
    this.codePage.set(1);
    this.reloadCodeValues();
  }

  goCodePrev(): void {
    if (this.codePage() <= 1) return;
    this.codePage.update((p) => p - 1);
    this.reloadCodeValues();
  }

  goCodeNext(): void {
    if (this.codePage() >= this.codeTotalPages()) return;
    this.codePage.update((p) => p + 1);
    this.reloadCodeValues();
  }

  goCodeLast(): void {
    if (this.codePage() >= this.codeTotalPages()) return;
    this.codePage.set(this.codeTotalPages());
    this.reloadCodeValues();
  }

  createHierarchy(): void {
    this.api
      .createHierarchyType({
        module: this.hierarchyForm.module,
        parentId: this.hierarchyForm.parentId ? Number(this.hierarchyForm.parentId) : null,
        code: this.hierarchyForm.code,
        name: this.hierarchyForm.name,
        level: this.hierarchyForm.level,
        sortOrder: Number(this.hierarchyForm.sortOrder) || 0,
        scope: 'global',
      })
      .subscribe({
        next: () => {
          this.showToast('Hierarchy node created');
          this.hierarchyForm.code = '';
          this.hierarchyForm.name = '';
          this.closeHierarchyModal();
          this.reload();
        },
        error: (err) => this.showToast(err?.error?.message || 'Create failed'),
      });
  }

  deactivateHierarchy(row: HierarchyType): void {
    if (!confirm(`Deactivate ${row.code}?`)) return;
    this.api.deleteHierarchyType(row.id).subscribe({
      next: () => {
        this.showToast('Deactivated');
        this.reload();
      },
      error: (err) => this.showToast(err?.error?.message || 'Delete failed'),
    });
  }

  createCodeValue(): void {
    this.api
      .createCodeValue({
        module: this.codeForm.module,
        codeType: this.codeForm.codeType,
        code: this.codeForm.code,
        label: this.codeForm.label,
        sortOrder: Number(this.codeForm.sortOrder) || 0,
        scope: 'global',
      })
      .subscribe({
        next: () => {
          this.showToast('Code value created');
          this.codeForm.code = '';
          this.codeForm.label = '';
          this.closeCodeModal();
          this.reload();
        },
        error: (err) => this.showToast(err?.error?.message || 'Create failed'),
      });
  }

  deactivateCode(row: SystemCodeValue): void {
    if (!confirm(`Deactivate ${row.code}?`)) return;
    this.api.deleteCodeValue(row.id).subscribe({
      next: () => {
        this.showToast('Deactivated');
        this.reload();
      },
      error: (err) => this.showToast(err?.error?.message || 'Delete failed'),
    });
  }

  private showToast(msg: string): void {
    this.toast.set(msg);
    setTimeout(() => this.toast.set(''), 3200);
  }
}
